import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../utils/fetchApi';
import { hashPassword } from '../utils/helpers';
import { useUser } from '../context/userContext';
import { useNavigate } from 'react-router-dom';
import arrowLeftIcon from '../assets/arrow-left.svg';
import InfoModal from '../components/InfoModal';
import successIcon from '../assets/modal/success.png';
import errorIcon from '../assets/modal/error.png';
import passwordCheckIcon from '../assets/password-check.svg';
import passwordErroricon from '../assets/password-error.svg';

export const PasswordChange: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccessChange, setIsSuccessChange] = useState(false);
  const [isFailedChange, setIsFailedChange] = useState(false);

  // Password validation criteria
  const passwordCriteria = {
    minLength: newPassword.length >= 8,
    hasLowercase: /[a-z]/.test(newPassword),
    hasUppercase: /[A-Z]/.test(newPassword),
    passwordsMatch: newPassword === confirmPassword && confirmPassword !== ''
  };

  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

  const { updatePassword } = useUser();

  const handleSave = async () => {
    setError('');

    if (!isPasswordValid) {
      setError(t('passwordChange.passwordTooWeak'));
      return;
    }

    const username = localStorage.getItem('username');
    if (!username) {
      setError(t('missingUsername'));
      return;
    }

    try {
      await updatePassword(username, newPassword);

      setNewPassword('');
      setConfirmPassword('');
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setIsSuccessChange(true);

    } catch (err) {
      setError(t('passwordChange.passwordChangeFailed'));
      console.error(err);
      setIsFailedChange(true);
    }
  };

  const handleBack = () => {
    navigate('/settings');
  };

  return (
    <div className="passwordChange">
      <div className="pageHeader">
        <button className="backButton" onClick={handleBack}>
          <img src={arrowLeftIcon} alt="back" />
        </button>
        <h3>{t('settings')}</h3>
      </div>

      <div className="passwordCard">
        <h2>{t('passwordChange.title')}</h2>

        {/* Password Requirements */}
        <div className="passwordRequirements">
          <h4>{t('passwordChange.passwordRequirements')}</h4>
          <div className="requirementsList">
            <div className={`requirement ${passwordCriteria.minLength ? 'valid' : 'invalid'}`}>
              <div className="requirementIcon">
                {passwordCriteria.minLength ? (
                  <img src={passwordCheckIcon} alt="password check" />
                ) : (
                  <img src={passwordErroricon} alt="password error" />
                )}
              </div>
              <span>{t('passwordChange.passwordMinLength')}</span>
            </div>

            <div className={`requirement ${passwordCriteria.hasLowercase ? 'valid' : 'invalid'}`}>
              <div className="requirementIcon">
                {passwordCriteria.hasLowercase ? (
                  <img src={passwordCheckIcon} alt="password check" />
                ) : (
                  <img src={passwordErroricon} alt="password error" />
                )}
              </div>
              <span>{t('passwordChange.passwordHasLowercase')}</span>
            </div>

            <div className={`requirement ${passwordCriteria.hasUppercase ? 'valid' : 'invalid'}`}>
              <div className="requirementIcon">
                {passwordCriteria.hasUppercase ? (
                  <img src={passwordCheckIcon} alt="password check" />
                ) : (
                  <img src={passwordErroricon} alt="password error" />
                )}
              </div>
              <span>{t('passwordChange.passwordHasUppercase')}</span>
            </div>

            <div className={`requirement ${passwordCriteria.passwordsMatch ? 'valid' : 'invalid'}`}>
              <div className="requirementIcon">
                {passwordCriteria.passwordsMatch ? (
                  <img src={passwordCheckIcon} alt="password check" />
                ) : (
                  <img src={passwordErroricon} alt="password error" />
                )}
              </div>
              <span>{t('passwordChange.passwordsMatch')}</span>
            </div>
          </div>
        </div>

        {/* New Password Input */}
        <div className="inputGroup">
          <label htmlFor="newPassword">{t('passwordChange.newPassword')}</label>
          <div className="passwordInput">
            <input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('passwordChange.enterNewPassword')}
            />
            <button
              type="button"
              className="passwordToggle"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                {showNewPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                ) : (
                  <>
                    <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Confirm Password Input */}
        <div className="inputGroup">
          <label htmlFor="confirmPassword">{t('passwordChange.confirmPassword')}</label>
          <div className="passwordInput">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('passwordChange.confirmNewPassword')}
            />
            <button
              type="button"
              className="passwordToggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                {showConfirmPassword ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1 1L23 23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                ) : (
                  <>
                    <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {error && <div className="errorMessage">{error}</div>}
        <button 
          className={`saveButton ${isPasswordValid ? 'enabled' : 'disabled'}`}
          onClick={handleSave}
          disabled={!isPasswordValid}
        >
          {t('passwordChange.save')}
        </button>
      </div>
      {isSuccessChange && (
        <InfoModal
          isOpen={isSuccessChange}
          onClose={() => setIsSuccessChange(false)}
          title="Sikeres jelszó frissítés"
          description="A jelszavad sikeresen megváltozott. Mostantól az új jelszót használhatod a bejelentkezéshez."
          icon={successIcon}
        />
      )}
      {isFailedChange && (
        <InfoModal
          isOpen={isFailedChange}
          onClose={() => setIsFailedChange(false)}
          title="Sikertelen jelszó frissítés"
          description="Hiba történt a jelszó módosítása közben. Kérlek, próbáld újra."
          icon={errorIcon}
        />
      )}
    </div>
  );
};
